import { useState } from "react";
import { PlusIcon, HashIcon, TableIcon, FileTextIcon } from "lucide-react";
import { AttributeKindEnum } from "@brewbase/db/types";

interface EmptyStateProps {
  onInsert: (position: number, kind: AttributeKindEnum) => void;
  isLocked: boolean;
  parametersOnly?: boolean;
  /** Singular noun for the insert control; defaults to Parameter/Attribute. */
  label?: string;
  /** One line, for one of several sections. */
  compact?: boolean;
}

export function EmptyState({
  onInsert,
  isLocked,
  parametersOnly,
  label,
  compact,
}: EmptyStateProps) {
  const [expanded, setExpanded] = useState(false);
  const noun = label ?? (parametersOnly ? "Parameter" : "Attribute");
  const nounLower = noun.toLowerCase();

  return (
    <div
      className={
        compact
          ? "flex items-center gap-3 border border-dashed border-border px-3 py-2 text-muted-foreground"
          : "flex flex-col items-center justify-center py-20 text-muted-foreground"
      }
    >
      <p className={compact ? "text-xs" : "text-sm mb-4"}>{`No ${nounLower}s.`}</p>
      {!isLocked &&
        (expanded ? (
          <div
            className="flex items-center gap-2"
            onMouseLeave={() => setExpanded(false)}
          >
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border hover:bg-muted transition-colors"
              onClick={() => onInsert(0, AttributeKindEnum.SINGLE_NUMBER)}
            >
              <HashIcon className="h-4 w-4" />
              {noun}
            </button>
            {!parametersOnly && (
              <>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border hover:bg-muted transition-colors"
                  onClick={() => onInsert(0, AttributeKindEnum.TABULAR_DATA)}
                >
                  <TableIcon className="h-4 w-4" />
                  Table
                </button>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border hover:bg-muted transition-colors"
                  onClick={() => onInsert(0, AttributeKindEnum.INSTRUCTION)}
                >
                  <FileTextIcon className="h-4 w-4" />
                  Instruction
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border hover:bg-muted transition-colors"
            onClick={() => setExpanded(true)}
          >
            <PlusIcon className="h-4 w-4" />
            {`Add first ${nounLower}`}
          </button>
        ))}
    </div>
  );
}
